import React, { useEffect, useState } from 'react';
import { LightbulbIcon, CheckCircleIcon } from 'lucide-react';
export const Solution = () => {
  const [currentDate, setCurrentDate] = useState('');
  useEffect(() => {
    // Format current date as "15 nov"
    const formatDate = () => {
      const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
      const now = new Date();
      const day = now.getDate();
      const month = months[now.getMonth()];
      return `${day} ${month}`;
    };
    setCurrentDate(formatDate());
  }, []);
  return <section className="mb-10" data-id="element-256">
      <div className="bg-blue-50 p-5 rounded-lg mb-8 border-l-4 border-blue-500" data-id="element-257">
        <h2 className="text-xl font-bold text-blue-800 flex items-center mb-4" data-id="element-258">
          <LightbulbIcon className="w-6 h-6 mr-2 text-blue-600" data-id="element-259" />
          De wetenschapper die ooit zei dat honden 'de baas willen zijn', zegt
          nu dat dit niet klopt.
        </h2>
        <p className="mb-4 leading-relaxed" data-id="element-260">
          David L. Mech is een van de bekende wetenschappers in de dierenwereld.
          Als wereldberoemd bioloog vormde zijn werk naar wolven uit de jaren
          '70 de basis waarop miljoenen hondeneigenaren met hun hond om gaan.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-261">
          Maar in 1999 deed Mech een ontdekking die alles veranderde. En
          sindsdien probeert hij wanhopig zijn eigen theorie te corrigeren.
        </p>
        <p className="text-lg font-semibold text-blue-800 mb-4" data-id="element-262">
          "Het probleem is dat mijn oorspronkelijke onderzoek werd uitgevoerd op
          wolven in gevangenschap," legt Mech uit in latere interviews. De
          conclusies uit dit onderzoek zijn daarom simpelweg niet van toepassing
          op de moderne hond. Het is absurd dat deze denkfout nog steeds leeft
          onder hondeneigenaren.
        </p>
      </div>
      <div className="w-full mb-6" data-id="element-263">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7ybqiWhtxfB1fDCg5RxnBJ/Scherm%C2%ADafbeelding_2025-09-01_om_23.36.13.png" alt="Hond die zich onderwerpt aan dominantie training in woonkamer" className="w-full h-auto rounded-lg shadow-md" data-id="element-264" />
        <p className="text-xs text-gray-500 mt-1 text-center italic" data-id="element-265">
          "Dominantie training is gebaseerd op verouderde wetenschap die de
          onderzoeker zelf heeft afgekeurd"
        </p>
      </div>
      <h2 className="text-xl font-bold text-blue-800 mb-4" data-id="element-266">
        Waarom 99% van de hondenbaasjes dit niet weten
      </h2>
      <p className="mb-6 leading-relaxed" data-id="element-267">
        Toen hij later wilde wolvenroedels in hun natuurlijke habitat
        bestudeerde, zag hij iets compleet anders. Geen agressieve alfa's die
        constant moesten vechten. Gewoon families, ouderparen met hun jongen die
        samenwerkten.
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-268">
        Nog belangrijker: ondanks dat wolven en honden verwant zijn, gedragen ze
        zich ook sociaal anders in groepen. Honden hebben een veel flexibelere
        sociale structuur.
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-269">
        "Het is een verouderd, achterhaald concept dat we 25 jaar geleden al
        hebben gecorrigeerd. Maar het concept blijft hangen, vooral in de
        hondentraining, en is moeilijk uit te roeien."
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-270">
        Maar deze toepassing is fout volgens Mech. "Het gebruik van
        'dominantietheorie' in hondentraining leidt tot aversieve methoden en
        een onjuist begrip van de hond-mens relatie. Een hond die op de bank
        springt, probeert niet de 'alfa' te zijn; Je hond is niet je rivaal die
        je moet 'domineren'"
      </p>
      <p className="mb-6 leading-relaxed font-semibold" data-id="element-271">
        De vraag is: als de grondlegger van de dominantie theorie deze al 25
        jaar probeert af te keuren, waarom weten baasjes dit dan nog niet?
      </p>
      <ul className="mb-6 space-y-3" data-id="element-272">
        <li className="flex items-start" data-id="element-273">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-274" />
          <span data-id="element-275">
            <strong data-id="element-276">Het lijkt soms te werken.</strong> Sommige honden laten zich
            wel africhten met dominantie, dat is het verwarrende voor veel
            baasjes. Dit geeft het gevoel dat de methode effectief zou zijn.
          </span>
        </li>
        <li className="flex items-start" data-id="element-277">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-278" />
          <span data-id="element-279">
            <strong data-id="element-280">Gemakkelijke marketing.</strong> Een afgerichte hond die
            exact doet wat je zegt ziet er stoer en krachtig uit.
          </span>
        </li>
        <li className="flex items-start" data-id="element-281">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-282" />
          <span data-id="element-283">
            <strong data-id="element-284">De trainings-industrie heeft er belang bij.</strong>{' '}
            Miljarden worden verdiend met dominantie-training.
          </span>
        </li>
      </ul>
      <p className="mb-6 leading-relaxed" data-id="element-285">
        Amee geeft al jaren training over hoe je op een fijne manier met je hond
        kunt samenleven zonder te domineren, straffen of corrigeren. Ze zegt
        hierover dat "samenwerken met je hond en soms naar zijn behoeftes
        luisteren misschien minder stoer klinkt. Maar volgens moderne wetenschap
        werkt dit wel veel beter."
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-286">
        Ook tijdens de week van {currentDate} geeft Amee een aantal online
        trainingen over deze nieuwe methode (
        <a href="https://stresslessdogs.nl/mcc-meta-adv3" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-287">
          aanmelden kan via deze link
        </a>
        ). Uit de reacties tijdens deze week blijkt dat haar uitleg over hoe je
        samenleeft zonder domineren, straffen en corrigeren voor veel
        hondeneigenaren een echte eye-opener is.
      </p>
    </section>;
};